<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <BasicCard
    :title="title || $t('uiMetrics.metrics')"
    :headerClass="headerClass"
  >
    <template #buttons>
      <PeriodSelectScroll
        v-if="allowPeriodSelect"
        v-model="selectedPeriod"
        :forCurrentAccount="true"
        class="flex-1-1"
        @loaded="onPeriodsLoaded($event)"
      />
    </template>

    <v-alert
      v-if="error"
      type="error"
      closable
    >
      {{ error }}
    </v-alert>

    <table v-else class="w-100">
      <tbody>
        <template v-if="positionType === 'vertical'">
          <tr
            v-for="item in list"
            :key="'pp_' + item.title"
          >
            <td
              :class="colPropertyClass"
            >
              {{ pt(item.alias, item.title) }}
            </td>
            <td
              :class="colValueClass"
            >
              <v-progress-circular
                v-if="loading"
                :data-loading="true"
                class="px-2"
                indeterminate
                color="primary"
                size="20"
              />
              <span v-else>{{ item.value ?? '-' }}</span>
            </td>
          </tr>
        </template>
        <template v-else-if="positionType === 'horizontal'">
          <tr>
            <template
              v-for="item in list"
              :key="'pp1_' + item.title"
            >
              <td
                :class="colPropertyClass"
              >
                {{ pt(item.alias, item.title) }}
              </td>
            </template>
          </tr>
          <tr>
            <template
              v-for="item in list"
              :key="'pp2_' + item.title"
            >
              <td
                :class="colValueClass"
              >
                <v-progress-circular
                  v-if="loading"
                  :data-loading="true"
                  indeterminate
                  color="primary"
                  size="20"
                />
                <span v-else>{{ item.value ?? '-' }}</span>
              </td>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </BasicCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, type Ref } from 'vue';
import { MetricsProps, defaults } from './Metrics.ts';

import { useAwaitAccountId, useLocalization } from '@/composables';
import { getMissingServerTranslations } from '@/utils/properties-translation';
import { PLanPropertyValueVM, apiErrorToString, useAccountPositionApi, AccountFullProfileVM } from '@ui-api3-sdk/api/api3';
import { PeriodSelectItem } from './base/PeriodSelect';
import { useAccountStore } from '@/pinia/useAccountStore';

import BasicCard from './base/BasicCard.vue';
import PeriodSelectScroll from './base/PeriodSelectScroll.vue';

const { pt } = useLocalization();

const props = withDefaults(defineProps<MetricsProps>(), {
  id: undefined,
  title: undefined,
  offset: undefined,
  numberPeriodsBack: undefined,
  allowPeriodSelect: undefined,
  treeId: undefined,
  headerClass: () => defaults.headerClass,
  accountProperties: () => defaults.accountProperties,
  positionType: () => defaults.positionType,
  colPropertyClass: () => defaults.colPropertyClass,
  colValueClass: () => defaults.colValueClass,
});

// data
interface List {
  title?: string,
  alias: string,
  value?: string | number | null,
  id: number,
}

const error = ref('');
const list: Ref<List[]> = ref(props.accountProperties.map((alias, index) => ({ alias, value: null, id: index })));
const loading: Ref<boolean> = ref(true);
const selectedPeriod = ref<PeriodSelectItem | undefined>(undefined);

// Разделяем свойства на маркетинговые (m.*) и профильные (p.*)
const marketingProperties = computed<string[]>(() =>
  props.accountProperties
    .filter(alias => alias.startsWith('m.'))
    .map(alias => alias.replace('m.', '')),
);

const profileProperties = computed<string[]>(() =>
  props.accountProperties
    .filter(alias => alias.startsWith('p.'))
    .map(alias => alias.replace('p.', '')),
);

const hasMarketingProperties = computed(() => marketingProperties.value.length > 0);
const hasProfileProperties = computed(() => profileProperties.value.length > 0);

onMounted(async () => {
  if (! props.allowPeriodSelect) {
    await fetch(props.numberPeriodsBack);
  }
});

function onPeriodsLoaded(periods: PeriodSelectItem[]) {

  if (props.numberPeriodsBack !== undefined) {
    const foundPeriod = periods.find(period => period.index === props.numberPeriodsBack);
    if (foundPeriod) {
      selectedPeriod.value = foundPeriod;
    }
  }

}

watch( selectedPeriod, (newValue) => {
  if (newValue) {
    fetch(undefined, newValue.id);
  }
});


const fetch = async (periodBack?: number, periodId?: number) => {
  loading.value = true;
  error.value = '';

  try {
    const currentAccountId = await useAwaitAccountId();
    const list_: List[] = [];

    // Загружаем маркетинговые свойства если есть
    if (hasMarketingProperties.value) {
      const positionResponse = await useAccountPositionApi().getPosition({
        id: currentAccountId,
        treeId: props.treeId,
        properties: marketingProperties.value,
        offset: props.offset,
        numberPeriodsBack: periodBack,
        periodId,
      });

      const marketingProps = (positionResponse.data.payload || [] as PLanPropertyValueVM[])
        .reduce((acc, position) => ([ ...acc, ...position.properties ]), [] as PLanPropertyValueVM[]);

      marketingProperties.value.forEach(alias => {
        const property = marketingProps.find(prop => prop.alias === alias);
        list_.push({
          title: property?.title || pt(`m.${alias}`),
          alias: `m.${alias}`,
          value: property?.value?.presentable,
          id: property?.id || -1,
        });
      });
    }

    // Загружаем профильные свойства текущего пользователя
    if (hasProfileProperties.value) {
      const currentAccount = await useAccountStore().getAccountAsync();
      const profile = currentAccount?.profile || [];

      profileProperties.value.forEach(alias => {
        const property = profile.find((prop: AccountFullProfileVM) => prop.alias === alias);
        list_.push({
          title: pt(`p.${alias}`),
          alias: `p.${alias}`,
          value: property?.value?.presentable,
          id: -1,
        });
      });
    }

    list.value = list_;
  } catch (e) {
    error.value = apiErrorToString(e);
  } finally {
    loading.value = false;
    getMissingServerTranslations();
  }
};
</script>
